import React, { useState, useRef, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { products, designElements, Product } from '../data/mockData';
import Canvas from './Canvas';
import CanvasToolbar from './CanvasToolbar';
import LeftSidebar from './LeftSidebar';
import PropertiesPanel from './PropertiesPanel';
import RightContentArea from './RightContentArea';
import PreviewWindow from './PreviewWindow';
import AIModal from './AIModal';
import SaveDesignModal from './SaveDesignModal';
import AIProcessingBubble from './AIProcessingBubble';
import { generateRandomCode } from '../utils/common';
import { TransformedLayer } from '../utils/resourceUtils';
import { request } from '../utils/request'; 
import {
  transformDesignForEditing,
  transformDesignBackToOriginal,
  cleanupExpiredCache,
  saveToLocalStorage,
  loadFromLocalStorage,
  loadFromHistory,
  getAllHistoryDesigns,
} from '../utils/designUtils';

interface DesignElement {
  id: string;
  type: 'text' | 'shape' | 'image';
  x: number;
  y: number;
  width: number;
  height: number;
  content?: string;
  fontSize?: number;
  fontFamily?: string;
  color?: string;
  shape?: string;
  visible: boolean;
}

interface AIGenerateResponse {
  code: string;
  src: string;
}


// 拖拽状态接口
interface DragState {
  isDragging: boolean;
  draggedLayerId: string | null;
  startX: number;
  startY: number;
  offsetX: number;
  offsetY: number;
}

// 控制点类型
type ControlPointType = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right' | 'top' | 'bottom' | 'left' | 'right';

// 变换状态接口
interface TransformState {
  isTransforming: boolean;
  transformType: 'scale' | 'rotate' | null;
  controlPoint: ControlPointType | null;
  startAngle: number;
  startDistance: number;
  startScale: number;
  initialDimension?: number; // 添加初始尺寸记录
  // New fields to support delta-based resizing (prevents size-proportional expansion)
  startMouseX?: number;
  startMouseY?: number;
  initialLeft?: number;
  initialTop?: number;
  initialWidth?: number;
  initialHeight?: number;
}


const DesignEditor: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = React.useState(() => new URLSearchParams(window.location.search));
  const cacheKey = searchParams.get('cacheKey');
  const product = products.find(p => p.id === id);
  const canvasRef = useRef<HTMLDivElement>(null);

  const [selectedTemplate, setSelectedTemplate] = useState<string>('');
  const [selectedColor, setSelectedColor] = useState(product?.colors[0]?.id || '');
  const [selectedSize, setSelectedSize] = useState(product?.sizes[0]?.id || '');
  const [elements, setElements] = useState<DesignElement[]>([]);
  const [selectedElement, setSelectedElement] = useState<string | null>(null);
  const [activeTool, setActiveTool] = useState<'select' | 'text' | 'shape'>('select');
  const [textContent, setTextContent] = useState('');
  const [fontSize, setFontSize] = useState(16);
  const [fontFamily, setFontFamily] = useState('Arial');
  const [textColor, setTextColor] = useState('#000000');
  const [selectedTemplateData, setSelectedTemplateData] = useState<any>(null);
  const [canvasSize, setCanvasSize] = useState({ width: 400, height: 500 });
  const [printArea, setPrintArea] = useState<any>(null);
  const [transformedLayers, setTransformedLayers] = useState<TransformedLayer[]>([]);

  // 保存设计相关状态
  const [isSaving, setIsSaving] = useState(false);
  const [saveDesignModal, setSaveDesignModal] = useState(false);
  const [currentProduct, setCurrentProduct] = useState<Product | undefined>(undefined);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [isPropertiesPanelOpen, setIsPropertiesPanelOpen] = useState(false);
  const [isAIModalOpen, setIsAIModalOpen] = useState(false);
  const [selectedTool, setSelectedTool] = useState('layers');

  // Preview Window 状态
  const [isPreviewWindowVisible, setIsPreviewWindowVisible] = useState(true);
  const [hasFabricActive, setHasFabricActive] = useState(false);
  const [fabricHistIndex, setFabricHistIndex] = useState(0);
  const [fabricHistLength, setFabricHistLength] = useState(0);
  const [fabricLayers, setFabricLayers] = useState<any[]>([]);
  const cacheInitRef = useRef(false);

  // Mobile detection
  const [isMobile, setIsMobile] = useState(false);

  // Touch gesture states
  const [touchState, setTouchState] = useState({
    isPinching: false,
    startDistance: 0,
    startScale: 1,
    startCenter: { x: 0, y: 0 }
  });

  // 全局桥接：允许其他组件（如 ResourcesPopup）在“自定义”页面更新当前选中图层的图片与 AI
  const GLOBAL_APPLY_RESOURCE_KEY = '__GiftMkr_applyResourceToSelectedLayer__';
  (globalThis as any)[GLOBAL_APPLY_RESOURCE_KEY] = (url: string, ai?: string) => {
    if (!selectedLayerId) {
      alert('Please select a layer to render.');
      return;
    }
    const selectedLayer = transformedLayers.find(l => l.id === selectedLayerId);
    console.log('selectedLayer', selectedLayer);
    if (!selectedLayer || (selectedLayer.type !== 'Image' && selectedLayer.type !== 'frame')) {
      alert('Please select an image or frame layer.');
      return;
    }
    try {
      setTransformedLayers(prevLayers =>
        prevLayers.map(layer =>
          (layer.id === selectedLayerId && (layer.type === 'Image' || layer.type === 'frame'))
            ? { ...layer, url, ai }
            : layer
        )
      );
    } catch (err) {
      console.warn('Apply resource to selected layer failed:', err);
    }
  };

  // Undo/Redo 状态
  const [history, setHistory] = useState<TransformedLayer[][]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [maxHistorySize] = useState(50); // 最大历史记录数量

  // 拖拽状态
  const [dragState, setDragState] = useState<DragState>({
    isDragging: false,
    draggedLayerId: null,
    startX: 0,
    startY: 0,
    offsetX: 0,
    offsetY: 0
  });

  // 鼠标按下状态
  const [isMouseDown, setIsMouseDown] = useState(false);

  // 拖拽时钟
  const dragTimerRef = useRef<NodeJS.Timeout | null>(null);

  // 选中的图层
  const [selectedLayerId, setSelectedLayerId] = useState<string | null>(null);

  // 变换状态
  const [transformState, setTransformState] = useState<TransformState>({
    isTransforming: false,
    transformType: null,
    controlPoint: null,
    startAngle: 0,
    startDistance: 0,
    startScale: 1
  });

  // 在 DesignEditor 中添加状态
  const [actualCanvasWidth, setActualCanvasWidth] = useState(canvasSize.width);
  const [historyInputValue, setHistoryInputValue] = useState('');
  const [showHistoryInput, setShowHistoryInput] = useState(false);
  const [selectedHistoryKey, setSelectedHistoryKey] = useState<string | null>(null);

  // 背景状态
  const [backgroundTexture, setBackgroundTextureState] = useState<string>('');
  const [backgroundImage, setBackgroundImageState] = useState<string>('');
  const [backgroundColor, setBackgroundColorState] = useState<string>('');

  // 在 useEffect 中获取实际宽度
  useEffect(() => {
    const updateActualWidth = () => {
      if (canvasRef.current) {
        const rect = canvasRef.current.getBoundingClientRect();
        console.log("rect", rect);
        setActualCanvasWidth(rect.width);
      }
    };

    updateActualWidth();
    window.addEventListener('resize', updateActualWidth);

    return () => {
      window.removeEventListener('resize', updateActualWidth);
    };
  }, [canvasSize]);

  // Mobile detection effect
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const handler = (e: Event) => {
      const d = (e as CustomEvent).detail || null;  
      setHasFabricActive(!!d);
      console.log(d)
    };
    window.addEventListener('giftmkr:activeObjectChanged', handler as EventListener);
    return () => window.removeEventListener('giftmkr:activeObjectChanged', handler as EventListener);
  }, []);
  useEffect(() => {
    const handler = (e: Event) => {
      const d = (e as CustomEvent).detail || {};
      console.log('historyChanged', d)
      setFabricHistIndex(d.index || 0);
      setFabricHistLength(d.length || 0);
    };
    window.addEventListener('giftmkr:', handler as EventListener);
    return () => window.removeEventListener('giftmkr:historyChanged', handler as EventListener);
  }, []);
  useEffect(() => {
    const handler = (e: Event) => {
      const d = (e as CustomEvent).detail || {};
      console.log('layersSnapshot', d)
      setFabricLayers(Array.isArray(d.objects) ? d.objects : []);
      console.log(d.objects)
      setTransformedLayers(d.objects);
      updateTemplateData(d.objects); 
    };

    window.addEventListener('giftmkr:layersSnapshot', handler as EventListener);
    return () => {
      window.removeEventListener('giftmkr:layersSnapshot', handler as EventListener);
    };
  }, []);



  // 更新template数据的函数
  const updateTemplateData = (updatedLayers: TransformedLayer[]) => {
    if (selectedTemplateData && currentProduct) { // 使用 currentProduct 而不是 product
      // 将transformedLayers转换回原始template格式 
      console.log("updatedLayers", JSON.stringify(updatedLayers));
      const updatedDesign = transformDesignBackToOriginal(updatedLayers, selectedTemplateData.design, canvasSize.width, canvasSize.height, {
        texture: backgroundTexture,
        image: backgroundImage,
        color: backgroundColor
      }); 
      
      // 更新currentProduct中的template数据
      const updatedProduct = {
        ...currentProduct, // 使用 currentProduct 作为基础
        templates: currentProduct.templates.map(template =>
          template.id === selectedTemplateData.id
            ? { ...template, design: updatedDesign }
            : template
        )
      }; 
      
      return updatedProduct;
    }
    return currentProduct; // 返回 currentProduct 而不是 product
  };


  // 修改template选择处理函数
  const handleTemplateSelect = (templateId: string) => {
    setSelectedTemplate(templateId);

    // 优先使用 currentProduct 的数据，如果有的话
    const activeProduct = currentProduct || product;
    const template = activeProduct?.templates.find(t => t.id === templateId); 
    if (template && template.design) {
      setSelectedTemplateData(template);

      // 获取 print_area 的宽高比
      const printArea = template.design.faces[0].print_area || { left: 0, top: 0, width: 1, height: 1 };
      const printAreaAspectRatio = printArea.width / printArea.height; // 宽高比

      // 计算画布尺寸（移动端适配）
      const isMobile = window.innerWidth < 1024;
      let containerWidth, containerHeight;

      if (isMobile) {
        // 移动端：基于屏幕宽度计算
        containerWidth = window.innerWidth - 32;
        containerHeight = containerWidth / printAreaAspectRatio; // 保持宽高比

        // 如果高度超出屏幕，则基于高度重新计算
        if (containerHeight > window.innerHeight * 0.6) {
          containerHeight = window.innerHeight * 0.6;
          containerWidth = containerHeight * printAreaAspectRatio;
        }
      } else {
        // 桌面端：基于固定尺寸计算
        const maxSize = 600;
        if (printAreaAspectRatio >= 1) {
          // 宽度大于等于高度，以宽度为基准
          containerWidth = maxSize;
          containerHeight = maxSize / printAreaAspectRatio;
        } else {
          // 高度大于宽度，以高度为基准
          containerHeight = maxSize;
          containerWidth = maxSize * printAreaAspectRatio;
        }
      }

      setCanvasSize({
        width: containerWidth,
        height: containerHeight
      });
      // 转换design数据为编辑格式
      console.log(template.design)
      const transformed = transformDesignForEditing(template.design, containerWidth, containerHeight);
      console.log("transformed", transformed);
      setTransformedLayers(transformed.layers);  
      // 初始化历史记录
      setHistory([transformed.layers]);
      setHistoryIndex(0);
    }
  };


  // 清理过期缓存的 effect
  useEffect(() => {
    if (cacheInitRef.current) return;
    cacheInitRef.current = true;
    if (cacheKey) {
      const cachedResult = loadFromLocalStorage(cacheKey);
      if (cachedResult) {
        console.log("cachedResult found .", cachedResult.templates[0].design.faces[0].layers);
        setCurrentProduct(cachedResult);
        setSelectedTool('layers');
      }
    }
    cleanupExpiredCache();
  }, []);

  // 初始化 currentProduct（只在没有 cacheKey 时使用原始产品数据）
  useEffect(() => {
    if (!cacheKey && product && !currentProduct) {
      setCurrentProduct(product);
    }
  }, [cacheKey, product, currentProduct]);

  // 当 currentProduct 确定后，自动选择第一个模板
  useEffect(() => {
    if (currentProduct && currentProduct.templates && currentProduct.templates.length > 0) {
      const firstTemplate = currentProduct.templates[0];
      if (firstTemplate && !selectedTemplate) {
        handleTemplateSelect(firstTemplate.id);
      }
    }
  }, [currentProduct, selectedTemplate]);

  // Touch gesture handlers
  const handleTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length === 2) {
      // Two finger gesture - pinch to zoom
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      const distance = Math.sqrt(
        Math.pow(touch2.clientX - touch1.clientX, 2) +
        Math.pow(touch2.clientY - touch1.clientY, 2)
      );
      const centerX = (touch1.clientX + touch2.clientX) / 2;
      const centerY = (touch1.clientY + touch2.clientY) / 2;

      setTouchState({
        isPinching: true,
        startDistance: distance,
        startScale: 1,
        startCenter: { x: centerX, y: centerY }
      });
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (touchState.isPinching && e.touches.length === 2) {
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      const currentDistance = Math.sqrt(
        Math.pow(touch2.clientX - touch1.clientX, 2) +
        Math.pow(touch2.clientY - touch1.clientY, 2)
      );

      const scale = currentDistance / touchState.startDistance;

    }
  };

  const handleTouchEnd = () => {
    setTouchState({
      isPinching: false,
      startDistance: 0,
      startScale: 1,
      startCenter: { x: 0, y: 0 }
    });
  };

  // 添加鼠标事件监听
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      // 如果正在变换，处理变换移动
      if (transformState.isTransforming) {
        handleTransformMove(e as any);
        return;
      }

      // 只有在鼠标按下且鼠标移动时才开始拖拽
      if (isMouseDown && dragState.draggedLayerId && !dragState.isDragging) {
        // 计算鼠标移动距离
        const deltaX = Math.abs(e.clientX - dragState.startX);
        const deltaY = Math.abs(e.clientY - dragState.startY);

        // 如果移动距离超过阈值，开始拖拽
        if (deltaX > 5 || deltaY > 5) {
          setDragState(prev => ({
            ...prev,
            isDragging: true
          }));
        }
      }

      // 如果正在拖拽，处理移动
      if (dragState.isDragging) {
        handleDragMove(e as any);
      }
    };

    const handleMouseUp = () => {
      // 如果正在变换，结束变换
      if (transformState.isTransforming) {
        handleTransformEnd();
        return;
      }

      // 鼠标松开时重置所有状态
      setIsMouseDown(false);
      if (dragState.isDragging || dragState.draggedLayerId) {
        handleDragEnd();
      }
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [dragState.isDragging, dragState.draggedLayerId, isMouseDown, transformState.isTransforming]);

  // 监听transformedLayers变化，同步更新template数据
  useEffect(() => {
    if (selectedTemplateData && transformedLayers.length > 0) {
      const updatedProduct = updateTemplateData(transformedLayers);
      console.log("updated current Product", updatedProduct);
      setCurrentProduct(updatedProduct);
    }
  }, [transformedLayers, selectedTemplateData]); // 移除 currentProduct 依赖项，避免死循环

  // 添加键盘事件监听
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Check if the user is focused on an input field, textarea, or select element
      const activeElement = document.activeElement as HTMLElement;
      const isInputFocused = activeElement && (
        activeElement.tagName === 'INPUT' ||
        activeElement.tagName === 'TEXTAREA' ||
        activeElement.tagName === 'SELECT' ||
        activeElement.contentEditable === 'true'
      );

      // If user is typing in an input field, don't process canvas shortcuts
      if (isInputFocused) return;

      if (!selectedLayerId) return;

      const selectedLayer = transformedLayers.find(layer => layer.id === selectedLayerId);
      if (!selectedLayer) return;

      const moveStep = e.shiftKey ? 10 : 1; // Shift键加速移动

      switch (e.key) {
        case 'Delete':
        case 'Backspace':
          e.preventDefault();
          deleteLayer(selectedLayerId);
          break;

        case 'ArrowUp':
          e.preventDefault();
          updateLayerPosition(selectedLayerId, selectedLayer.left, selectedLayer.top - moveStep);
          break;

        case 'ArrowDown':
          e.preventDefault();
          updateLayerPosition(selectedLayerId, selectedLayer.left, selectedLayer.top + moveStep);
          break;

        case 'ArrowLeft':
          e.preventDefault();
          updateLayerPosition(selectedLayerId, selectedLayer.left - moveStep, selectedLayer.top);
          break;

        case 'ArrowRight':
          e.preventDefault();
          updateLayerPosition(selectedLayerId, selectedLayer.left + moveStep, selectedLayer.top);
          break;

        case '=':
        case '+':
          e.preventDefault();
          // 放大图层
          const scaleUp = 1.1;
          updateLayerSize(
            selectedLayerId,
            selectedLayer.width * scaleUp,
            selectedLayer.height * scaleUp
          );
          // 如果是文本图层，根据滑杆边界调整fontSize
          if (selectedLayer.type === 'text') {
            setTransformedLayers(prevLayers =>
              prevLayers.map(layer =>
                layer.id === selectedLayerId
                  ? {
                    ...layer,
                    fontSize: Math.min(
                      selectedLayer.width * 0.8, // 宽度限制，留10%边距
                      selectedLayer.height * 0.8  // 高度限制，留10%边距
                    )
                  }
                  : layer
              )
            );
          }
          break;

        case '-':
          e.preventDefault();
          // 缩小图层
          const scaleDown = 0.9;
          updateLayerSize(
            selectedLayerId,
            selectedLayer.width * scaleDown,
            selectedLayer.height * scaleDown
          );
          // 如果是文本图层，根据滑杆边界调整fontSize
          if (selectedLayer.type === 'text') {
            setTransformedLayers(prevLayers =>
              prevLayers.map(layer =>
                layer.id === selectedLayerId
                  ? {
                    ...layer,
                    fontSize: Math.max(8, Math.min(300, Math.min(
                      selectedLayer.width * 0.8, // 宽度限制，留10%边距
                      selectedLayer.height * 0.8  // 高度限制，留10%边距
                    )))
                  }
                  : layer
              )
            );
          }
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedLayerId, transformedLayers]);

  // 历史记录管理函数
  const saveToHistory = (layers: TransformedLayer[]) => {
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push([...layers]);

    // 限制历史记录数量
    if (newHistory.length > maxHistorySize) {
      newHistory.shift();
    }

    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
  };

  const undo = () => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      setHistoryIndex(newIndex);
      setTransformedLayers([...history[newIndex]]);
    }
  };

  const redo = () => {
    if (historyIndex < history.length - 1) {
      const newIndex = historyIndex + 1;
      setHistoryIndex(newIndex);
      setTransformedLayers([...history[newIndex]]);
    }
  };

  // 图层层级控制函数
  const moveLayerForward = () => {
    window.dispatchEvent(new CustomEvent('giftmkr:bringToFront'));
  };

  const moveLayerBackward = () => {
    const ev = new CustomEvent('giftmkr:align', { detail: { op: 'center' } });
    window.dispatchEvent(ev);
  };

  // 图层拖拽重排序函数
  const reorderLayers = (fromIndex: number, toIndex: number) => {

  };

  // 早期返回必须在所有hooks之后
  if (!currentProduct) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Product not found</h2>
          <Link
            to="/products"
            className="transition-colors"
            style={{ color: '#2e3a47' }}
            onMouseEnter={(e) => e.currentTarget.style.color = '#3a4a57'}
            onMouseLeave={(e) => e.currentTarget.style.color = '#2e3a47'}
          >
            Back to products
          </Link>
        </div>
      </div>
    );
  }

  const selectedColorObj = currentProduct?.colors.find(c => c.id === selectedColor);

 
  const addTextElement = () => {
    const text = textContent || 'Add your text here';
    const ev = new CustomEvent('giftmkr:addText', { detail: { text, fontSize, fontFamily, fill: textColor } });
    window.dispatchEvent(ev);
  };

  const addShapeElement = (shape: string) => {
    const ev = new CustomEvent('giftmkr:addShape', { detail: { shape } });
    window.dispatchEvent(ev);
  };

  const addFrameElement = (frameStyle: string, frameColor: string, frameSize: number) => {
    const url = 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=400&h=400&fit=crop&crop=center';
    const ev = new CustomEvent('giftmkr:addImage', { detail: { url, borderColor: frameColor || '#000000', borderSize: typeof frameSize === 'number' ? frameSize : 4, frameStyle } });
    window.dispatchEvent(ev);
  };


  // 计算合适的显示尺寸（长边占画布的80%）
  const calculateDisplaySize = (imageWidth: number, imageHeight: number, canvasWidth: number, canvasHeight: number) => {
    const maxDisplaySize = Math.min(canvasWidth, canvasHeight) * 0.8; // 长边占画布的80%

    // 计算宽高比
    const aspectRatio = imageWidth / imageHeight;

    let displayWidth, displayHeight;

    if (imageWidth > imageHeight) {
      // 图片是横向的，以宽度为基准
      displayWidth = maxDisplaySize;
      displayHeight = maxDisplaySize / aspectRatio;
    } else {
      // 图片是纵向的，以高度为基准
      displayHeight = maxDisplaySize;
      displayWidth = maxDisplaySize * aspectRatio;
    }

    return {
      width: Math.round(displayWidth),
      height: Math.round(displayHeight)
    };
  };

  const addImageElement = async (imageUrl: string) => {
    const ev = new CustomEvent('giftmkr:addImage', { detail: { url: imageUrl + "?x-oss-process=style/large", borderColor: '#000000' } });
    window.dispatchEvent(ev);
  };

  // Background functions
  const setBackgroundTexture = (texture: string) => {
    setBackgroundColorState('');
    setBackgroundImageState('');
    setBackgroundTextureState(texture);
    const ev = new CustomEvent('giftmkr:setBackgroundTexture', { detail: { texture } });
    window.dispatchEvent(ev);
  };

  const setBackgroundImage = (imageUrl: string) => {
    setBackgroundColorState('');
    setBackgroundTextureState('');
    setBackgroundImageState(imageUrl);
    const ev = new CustomEvent('giftmkr:setBackgroundImage', { detail: { url: imageUrl } });
    window.dispatchEvent(ev);

  };

  const uploadBackgroundImage = (file: File) => {
    // Handle file upload for background
    const reader = new FileReader();
    reader.onload = (e) => {
      const imageUrl = e.target?.result as string;
      setBackgroundTextureState('');
      setBackgroundColorState('');
      setBackgroundImageState(imageUrl);
      const ev = new CustomEvent('giftmkr:setBackgroundImage', { detail: { url: imageUrl } });
      window.dispatchEvent(ev);
    };
    reader.readAsDataURL(file);
  };

  const setBackgroundColor = (color: string) => {
    setBackgroundColorState(color);
    const ev = new CustomEvent('giftmkr:setBac groundColor', { detail: { color } });
    window.dispatchEvent(ev);
  };


  // Alignment functions
  const alignLayerLeft = () => {
    const ev = new CustomEvent('giftmkr:align', { detail: { op: 'left' } });
    window.dispatchEvent(ev);
  };

  const alignLayerCenter = () => {
    const ev  = new CustomEvent('giftmkr:align', { detail: { op: 'center' } });
    window.dispatchEvent(ev);
  };

  const lockLayer = (layerId: string) => {
    const ev = new CustomEvent('giftmkr:lockLayer');
    window.dispatchEvent(ev);
  };


  const unlockLayer = (layerId: string) => {
    const ev = new CustomEvent('giftmkr:unlockLayer');
    window.dispatchEvent(ev);
  };

  const alignLayerRight = () => {
    const ev = new CustomEvent('giftmkr:align', { detail: { op: 'right' } });
    window.dispatchEvent(ev);
  };

  const alignLayerTop = () => {
    const ev = new CustomEvent('giftmkr:align', { detail: { op: 'top' } });
    window.dispatchEvent(ev);
  };

  const alignLayerMiddle = () => {
    const ev = new CustomEvent('giftmkr:align', { detail: { op: 'middle' } });
    window.dispatchEvent(ev);
  };

  const alignLayerBottom = () => {
    const ev = new CustomEvent('giftmkr:align', { detail: { op: 'bottom' } });
    window.dispatchEvent(ev);
  };

  // AI Generation function
  const handleAIGeneration = (styleModel: string, description: string) => {
    if (!selectedLayerId) return;

    const selectedLayer = transformedLayers.find(layer => layer.id === selectedLayerId);
    if (!selectedLayer || (selectedLayer.type !== 'Image' && selectedLayer.type !== 'frame')) {
      console.log('AI enhancement only available for image and frame layers');
      return;
    }

    // Here you would typically make an API call to an AI service
    console.log('AI Generation:', {
      layerId: selectedLayerId,
      layerType: selectedLayer.type,
      styleModel,
      description
    });

    // For now, just show a success message
    alert(`AI enhancement applied with ${styleModel} style: ${description}`);
  };

  const deleteElement = (elementId: string) => {
    setElements(elements.filter(el => el.id !== elementId));
    setSelectedElement(null);
  };

  const toggleElementVisibility = (elementId: string) => {
    setElements(elements.map(el =>
      el.id === elementId ? { ...el, visible: !el.visible } : el
    ));
  };

  const updateElementPosition = (elementId: string, x: number, y: number) => {
    setElements(elements.map(el =>
      el.id === elementId ? { ...el, x, y } : el
    ));
  };

  const handleCanvasClick = (e?: React.MouseEvent) => {
    // 只有在select工具模式下，点击空白区域取消选中
    console.log("xxxxxxxxxxx1.....")
    if (activeTool === 'select') {
        setSelectedLayerId(null);
    }
    // 移除了在画布上直接添加元素的逻辑
  };

  // // 处理图层选中
  const handleLayerClick = (layerId: string, e?: React.MouseEvent) => {
 
    setSelectedLayerId(layerId);

    // 如果选中的是文本图层，自动切换到text工具并绑定文本编辑器的值
    const selectedLayer = transformedLayers.find(layer => layer.id === layerId);
    if (selectedLayer && selectedLayer.type === 'text') {
      setActiveTool('text');
      // 绑定文本编辑器的值到选中的图层
      setTextContent(selectedLayer.text || '');
      setFontSize(selectedLayer.fontSize || 16);
      setFontFamily(selectedLayer.fontFamily || 'Arial');
      setTextColor(selectedLayer.fill || '#000000');
    }
  };

  // 处理拖拽开始
  const handleDragStart = (e: React.MouseEvent, layerId: string) => {
    e.stopPropagation();
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;

    // 找到当前图层
    const currentLayer = transformedLayers.find(layer => layer.id === layerId);
    if (!currentLayer) return;

    // 计算鼠标相对于图层左上角的偏移量
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;
    const offsetX = mouseX - currentLayer.left;
    const offsetY = mouseY - currentLayer.top;

    // 设置鼠标按下状态
    setIsMouseDown(true);

    setDragState({
      isDragging: true, // 初始时不拖拽，等待鼠标移动
      draggedLayerId: layerId,
      startX: e.clientX,
      startY: e.clientY,
      offsetX: offsetX,
      offsetY: offsetY
    });
  };

  // 处理拖拽移动
  const handleDragMove = (e: React.MouseEvent) => {
    if (!dragState.isDragging || !dragState.draggedLayerId) return;

    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;

    // 使用保存的偏移量计算新位置
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;
    const newX = mouseX - dragState.offsetX;
    const newY = mouseY - dragState.offsetY;

    // 更新图层位置 - 允许超出画布
    const newLayers = transformedLayers.map(layer =>
      layer.id === dragState.draggedLayerId
        ? {
          ...layer,
          left: newX,
          top: newY
        }
        : layer
    );
    setTransformedLayers(newLayers);
  };

  // 处理拖拽结束
  const handleDragEnd = () => {
    // 保存到历史记录
    saveToHistory(transformedLayers);

    setDragState({
      isDragging: false,
      draggedLayerId: null,
      startX: 0,
      startY: 0,
      offsetX: 0,
      offsetY: 0
    });
    setIsMouseDown(false);
  };

  // 计算两点之间的距离
  const getDistance = (x1: number, y1: number, x2: number, y2: number) => {
    return Math.sqrt((x2 - x1) ** 2 + (y2 - y1) ** 2);
  };

  // 计算两点之间的角度
  const getAngle = (x1: number, y1: number, x2: number, y2: number) => {
    return Math.atan2(y2 - y1, x2 - x1);
  };

  // 处理变换开始
  const handleTransformStart = (e: React.MouseEvent, controlPoint: ControlPointType) => {
    e.stopPropagation();
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect || !selectedLayerId) return;

    const selectedLayer = transformedLayers.find(layer => layer.id === selectedLayerId);
    if (!selectedLayer) return;

    // 计算图层中心点
    const centerX = selectedLayer.left + selectedLayer.width / 2;
    const centerY = selectedLayer.top + selectedLayer.height / 2;

    // 计算鼠标相对于中心点的位置
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;

    // 根据控制点类型计算不同的起始距离
    let startDistance: number;
    let startAngle: number;
    let initialDimension: number = 0;

    if (['top', 'bottom', 'left', 'right'].includes(controlPoint)) {
      // 对于单向拉伸，记录初始尺寸和鼠标位置
      if (controlPoint === 'top' || controlPoint === 'bottom') {
        initialDimension = selectedLayer.height;
        startDistance = Math.abs(mouseY - centerY);
      } else {
        initialDimension = selectedLayer.width;
        startDistance = Math.abs(mouseX - centerX);
      }
      startAngle = getAngle(centerX, centerY, mouseX, mouseY);
    } else {
      // 对于角点控制，使用到中心点的距离
      startDistance = getDistance(centerX, centerY, mouseX, mouseY);
      startAngle = getAngle(centerX, centerY, mouseX, mouseY);
    }

    setTransformState({
      isTransforming: true,
      transformType: 'scale',
      controlPoint,
      startAngle: startAngle,
      startDistance: startDistance,
      startScale: 1,
      initialDimension: initialDimension,
      // Delta-based fields
      startMouseX: mouseX,
      startMouseY: mouseY,
      initialLeft: selectedLayer.left,
      initialTop: selectedLayer.top,
      initialWidth: selectedLayer.width,
      initialHeight: selectedLayer.height
    });
  };

  // 处理变换移动
  const handleTransformMove = (e: React.MouseEvent) => {
    if (!transformState.isTransforming || !selectedLayerId) return;

    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;

    const cw = rect.width;
    const ch = rect.height;

    const selectedLayer = transformedLayers.find(layer => layer.id === selectedLayerId);
    if (!selectedLayer) return;

    // 计算图层中心点
    const centerX = selectedLayer.left + selectedLayer.width / 2;
    const centerY = selectedLayer.top + selectedLayer.height / 2;

    // 计算当前鼠标相对于中心点的位置
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;
    const currentDistance = getDistance(centerX, centerY, mouseX, mouseY);
    const currentAngle = getAngle(centerX, centerY, mouseX, mouseY);

    // 根据控制点类型进行不同的变换
    // 先使用像素空间进行计算，最后再转换回归一化坐标
    let newWidthPx = selectedLayer.width * cw;
    let newHeightPx = selectedLayer.height * ch;
    let newLeftPx = selectedLayer.left * cw;
    let newTopPx = selectedLayer.top * ch;
    let newTransform = selectedLayer.transform || 'none';

    switch (transformState.controlPoint) {
      case 'top-left':
      case 'top-right':
      case 'bottom-left':
      case 'bottom-right':
        // 角点控制 - 等比缩放（保持现有逻辑）
        {
          const startWpx = (transformState.initialWidth ?? selectedLayer.width) * cw;
          const startHpx = (transformState.initialHeight ?? selectedLayer.height) * ch;
          const scaleRatio = currentDistance / transformState.startDistance;
          const newScale = Math.max(0.1, Math.min(5, scaleRatio));
          newWidthPx = startWpx * newScale;
          newHeightPx = startHpx * newScale;
          newLeftPx = centerX * cw - newWidthPx / 2;
          newTopPx = centerY * ch - newHeightPx / 2;
          newTransform = `rotate(${currentAngle - transformState.startAngle}rad)`;
        }
        break;

      case 'top':
      case 'bottom':
        // 垂直拉伸 - 使用与起始点的位移（delta）计算，避免尺寸越大放大越多
        {
          const startY = transformState.startMouseY ?? mouseY;
          const deltaY = mouseY - startY; // 像素
          const initialHpx = (transformState.initialHeight ?? selectedLayer.height) * ch;
          const initialTpx = (transformState.initialTop ?? selectedLayer.top) * ch;
          if (transformState.controlPoint === 'bottom') {
            newHeightPx = Math.max(10, initialHpx + deltaY);
            newTopPx = initialTpx; // bottom 拉伸不改变 top
          } else {
            // top 控制点：top 随着拉伸向上/下移动，底边固定
            newHeightPx = Math.max(10, initialHpx - deltaY);
            newTopPx = initialTpx + deltaY;
          }
        }
        break;

      case 'left':
      case 'right':
        // 水平拉伸 - 使用位移（delta）计算，避免随尺寸放大
        {
          const startX = transformState.startMouseX ?? mouseX;
          const deltaX = mouseX - startX; // 像素
          const initialWpx = (transformState.initialWidth ?? selectedLayer.width) * cw;
          const initialLpx = (transformState.initialLeft ?? selectedLayer.left) * cw;
          if (transformState.controlPoint === 'right') {
            newWidthPx = Math.max(10, initialWpx + deltaX);
            newLeftPx = initialLpx; // 右侧拉伸不改变 left
          } else {
            // 左侧控制点：left 随着拉伸移动，右边固定
            newWidthPx = Math.max(10, initialWpx - deltaX);
            newLeftPx = initialLpx + deltaX;
          }
        }
        break;
    }

    // 将像素值转换回归一化坐标
    const newWidth = newWidthPx / cw;
    const newHeight = newHeightPx / ch;
    const newLeft = newLeftPx / cw;
    const newTop = newTopPx / ch;

    // 更新图层 - 允许超出画布
    const newLayers = transformedLayers.map(layer =>
      layer.id === selectedLayerId
        ? {
          ...layer,
          width: newWidth,
          height: newHeight,
          left: newLeft,
          top: newTop,
          transform: newTransform,
        }
        : layer
    );
    setTransformedLayers(newLayers);
  };

  // 处理变换结束
  const handleTransformEnd = () => {
    // 保存到历史记录
    saveToHistory(transformedLayers);

    setTransformState({
      isTransforming: false,
      transformType: null,
      controlPoint: null,
      startAngle: 0,
      startDistance: 0,
      startScale: 1
    });
  };

  // 更新图层位置
  const updateLayerPosition = (layerId: string, x: number, y: number) => {
    console.log('updateLayerPosition', layerId, x, y);
    const newLayers = transformedLayers.map(layer =>
      layer.id === layerId
        ? {
          ...layer,
          left: x,
          top: y
        }
        : layer
    );

    setTransformedLayers(newLayers); 
    // 保存到历史记录
    saveToHistory(newLayers);
  };

  // 更新图层尺寸
  const updateLayerSize = (layerId: string, width: number, height: number) => {
    console.log('updateLayerSize', layerId, width, height);
    // 确保最小尺寸
    const minSize = 10;
    const finalWidth = Math.max(width, minSize);
    const finalHeight = Math.max(height, minSize);

    const newLayers = transformedLayers.map(layer =>
      layer.id === layerId
        ? {
          ...layer,
          width: finalWidth,
          height: finalHeight
        }
        : layer
    );
    setTransformedLayers(newLayers);
    // 保存到历史记录
    saveToHistory(newLayers);
  };

  // 新增：更新图层旋转角度（度数）
  const updateLayerRotation = (layerId: string, rotation: number) => {
    const newLayers = transformedLayers.map(layer =>
      layer.id === layerId
        ? {
          ...layer,
          angle: rotation
        }
        : layer
    );
    setTransformedLayers(newLayers);
    saveToHistory(newLayers);
  };

  // 删除图层
  const deleteLayer = (layerId: string) => {
    const layer = transformedLayers.find(l => l.id === layerId);
    if (layer) {
      // if (window.confirm(`Are you sure you want to delete this ${layerName}?`)) {
      const newLayers = transformedLayers.filter(layer => layer.id !== layerId);
      setTransformedLayers(newLayers);
      setSelectedLayerId(null);
      // 保存到历史记录
      saveToHistory(newLayers);
      // }
    }
  };

  // 产品选择处理函数
  const handleProductSelect = (selectedProduct: Product) => {
    setCurrentProduct(selectedProduct);
    setSelectedTool('layers'); // 切换到 layers 工具

    // 自动选择第一个template
    if (selectedProduct.templates && selectedProduct.templates.length > 0) {
      const firstTemplate = selectedProduct.templates[0];
      handleTemplateSelect(firstTemplate.id);
    }
  };


  // 保存设计到API的函数
  const saveDesignToAPI = async (productData: Product | undefined, name: string, cacheKey: string | null = null) => {
    if (!productData) {
      alert('No design to save');
      return false;
    }

    setIsSaving(true);
    try {
      // 生成新的缓存键
      const newCacheKey = generateRandomCode();

      // 准备设计数据
      const designData = {
        name,
        category: 'custom', // 使用默认分类
        image: productData.templates[0]?.preview || '', // 使用模板预览图
        isPublic: false, // 默认私有 
        designData: { "templates": productData.templates },
        productId: productData.id ? parseInt(productData.id) : null,
        lastCode: cacheKey || null, // 使用之前的缓存键作为 lastCode
        code: newCacheKey, // 新的缓存键
        metadata: {
          cacheKey: newCacheKey,
          savedAt: new Date().toISOString(),
          canvasSize,
          printArea
        }
      };

      const response: any = await request({
        method: 'POST',
        url: 'saved-designs',
        data: designData
      });

      if (response.success) {
        setSaveDesignModal(false);
        return newCacheKey;
      } else {
        alert('Failed to save design: ' + (response.message || 'Unknown error'));
        return false;
      }
    } catch (error) {
      console.error('Save design to API error:', error);
      alert('Failed to save design to server');
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  // 处理保存设计的函数
  const handleSaveDesign = async (designName: string) => {
    const updatedProduct = updateTemplateData(transformedLayers);
    await saveDesignToAPI(updatedProduct, designName);
  };



  return (
    <div className="h-screen bg-gray-100 flex flex-col">
      {/* Top Header Bar */}
      <div className="text-white px-4 lg:px-6 py-3 flex items-center justify-between" style={{ backgroundColor: '#2e3a47' }}>
        <div className="flex items-center space-x-2 lg:space-x-4">
          <div className="text-white text-xl lg:text-2xl font-bold">Editor</div>
          <h1 className="text-sm lg:text-lg font-semibold hidden sm:block">{currentProduct?.name || product?.name || 'Product'}</h1>
        </div>
        <div className="flex items-center space-x-2 lg:space-x-6">
          {/* Mobile Menu Button */}
          {isMobile && (
            <button
              onClick={() => setIsMobileSidebarOpen(!isMobileSidebarOpen)}
              className="lg:hidden p-2 rounded transition-colors"
              style={{ backgroundColor: '#68aeb0' }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#7bb8ba'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#68aeb0'}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          )}

          <div className="flex items-center space-x-2 lg:space-x-3">
            {/* <button className="px-2 lg:px-4 py-1 lg:py-2 bg-gray-700 text-white rounded hover:bg-gray-600 text-xs lg:text-sm">Share</button> */}
            {/* <button
              onClick={() => {
                setSaveDesignModal(true);
              }}
              className="px-3 lg:px-6 py-1 lg:py-2 text-white rounded transition-colors text-xs lg:text-sm mr-2"
              style={{ backgroundColor: '#68aeb0' }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#7bb8ba'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#68aeb0'}
            >
              Save Design
            </button> */}
            <button
              onClick={async () => {
                // 确保保存最新的transformedLayers到currentProduct  
                setTransformedLayers(fabricLayers);
                const updatedProduct = updateTemplateData(fabricLayers); 
                const newCacheKey = await saveDesignToAPI(updatedProduct, 'custom', cacheKey);
                if (newCacheKey) saveToLocalStorage(updatedProduct, newCacheKey); // 使用默认分类om
                // 跳转到产品详情页面，传递缓存键
                if (newCacheKey && currentProduct) {
                  window.location.href = `/product/${currentProduct.id}/${newCacheKey}`;
                }
              }}
              className="px-3 lg:px-6 py-1 lg:py-2 text-white rounded transition-colors text-xs lg:text-sm"
              style={{ backgroundColor: '#eb9072' }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f0a085'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#eb9072'}
            >
              Done
            </button>


            {/* AI Processing Bubble plugin */}
            <AIProcessingBubble className="fixed top-16 right-4 z-[1000] inline-block w-fit"  />
            {/* <button
              onClick={() => {
                setShowHistoryInput(!showHistoryInput);
                if (showHistoryInput) {
                  setHistoryInputValue('');
                  setSelectedHistoryKey(null);
                }
              }}
              className="px-3 lg:px-6 py-1 lg:py-2 text-white rounded transition-colors text-xs lg:text-sm"
              style={{ backgroundColor: '#68aeb0' }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#7bb8ba'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#68aeb0'}
            >
              History
            </button> */}
          </div>

          {/* History Popup */}
          {showHistoryInput && (
            <>
              {/* Backdrop */}
              <div
                className="fixed inset-0 bg-black bg-opacity-50 z-[999999]"
                onClick={() => {
                  setShowHistoryInput(false);
                  setHistoryInputValue('');
                  setSelectedHistoryKey(null);
                }}
              />

              {/* History Popup */}
              <div className="fixed top-20 right-0 md:right-8 lg:right-12 z-[999999]">
                <div className="bg-white rounded-lg shadow-xl border border-gray-200 w-full max-w-md md:w-96">

                  <div className="flex items-center justify-between p-3 sm:p-4 border-b border-gray-200">
                    <h3 className="text-base sm:text-lg font-bold text-gray-900">
                      History Designs
                    </h3>
                    <button
                      onClick={() => {
                        setShowHistoryInput(false);
                        setHistoryInputValue('');
                        setSelectedHistoryKey(null);
                      }}
                      className="text-gray-400 hover:text-gray-600 transition-colors p-1"
                      aria-label="Close history"
                    >
                      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>


                  {/* History Content */}
                  <div className="p-3 sm:p-4">
                    {(() => {
                      const designs = getAllHistoryDesigns();
                      const filteredDesigns = designs.filter(design =>
                        design.key.toLowerCase().includes(historyInputValue.toLowerCase()) ||
                        design.product.name.toLowerCase().includes(historyInputValue.toLowerCase())
                      );

                      if (filteredDesigns.length === 0) {
                        return (
                          <div className="text-center py-6 sm:py-8">
                            <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                              <svg className="h-8 w-8 sm:h-10 sm:w-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                            </div>
                            <p className="text-gray-500 mb-3 sm:mb-4 text-sm sm:text-base">
                              {historyInputValue ? 'No matching designs found' : 'No history designs found'}
                            </p>
                          </div>
                        );
                      }

                      return (
                        <div className="space-y-3 sm:space-y-4">
                          {/* Results Count */}
                          <div className="text-xs text-gray-500">
                            Found {filteredDesigns.length} design{filteredDesigns.length !== 1 ? 's' : ''}
                          </div>

                          {/* History Items List */}
                          {filteredDesigns.map(design => {
                            const timestamp = new Date(design.timestamp);
                            const isSelected = selectedHistoryKey === design.key;

                            return (
                              <div
                                key={design.key}
                                className={`flex items-center space-x-3 p-3 rounded-lg cursor-pointer transition-colors ${isSelected
                                  ? 'border'
                                  : 'hover:bg-gray-50 border border-transparent'
                                  }`}
                                style={isSelected ? { backgroundColor: '#f0f2f5', borderColor: '#2e3a47' } : {}}
                                onClick={() => {
                                  setSelectedHistoryKey(design.key);
                                }}
                              >
                                <div className="w-12 h-12 bg-gray-200 rounded flex items-center justify-center flex-shrink-0">
                                  <span className="text-lg">🎨</span>
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="text-sm font-medium text-gray-900 truncate">
                                    {design.product.name}
                                  </div>
                                  <div className="text-xs text-gray-500 mt-1">
                                    <div className="truncate">{design.key}</div>
                                    <div>{timestamp.toLocaleDateString()}</div>
                                  </div>
                                </div>
                                {isSelected && (
                                  <div style={{ color: '#2e3a47' }}>
                                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                    </svg>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      );
                    })()}
                  </div>

                  {/* Footer */}
                  <div className="p-3 sm:p-4 border-t border-gray-200">
                    <button
                      onClick={() => {
                        if (selectedHistoryKey) {
                          loadFromHistory(selectedHistoryKey, true);
                          setShowHistoryInput(false);
                          setHistoryInputValue('');
                          setSelectedHistoryKey(null);
                        }
                      }}
                      disabled={!selectedHistoryKey}
                      className={`w-full py-2 px-4 rounded-lg font-medium text-sm transition-colors ${selectedHistoryKey
                        ? 'text-white'
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        }`}
                      style={selectedHistoryKey ? { backgroundColor: '#2e3a47' } : {}}
                      onMouseEnter={selectedHistoryKey ? (e) => e.currentTarget.style.backgroundColor = '#3a4a57' : undefined}
                      onMouseLeave={selectedHistoryKey ? (e) => e.currentTarget.style.backgroundColor = '#2e3a47' : undefined}
                    >
                      Load Selected Design
                    </button>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex flex-1 overflow-hidden relative">
        {/* Mobile Sidebar Overlay */}
        {isMobile && isMobileSidebarOpen && (
          <div className="fixed inset-0 z-50 lg:hidden">
            <div className="absolute inset-0 bg-black bg-opacity-50" onClick={() => setIsMobileSidebarOpen(false)}></div>
            <div className="absolute left-0 top-0 h-full w-80 bg-white shadow-xl flex">
              {/* Left Sidebar */}
              <div className="w-24 bg-gray-800 text-white h-full">
                <LeftSidebar
                  selectedTool={selectedTool}
                  onToolSelect={(tool) => {
                    setSelectedTool(tool);
                  }}
                />
              </div>

              {/* Right Content Area */}
              <div className="flex-1 h-full">
                <RightContentArea
                  selectedTool={selectedTool}
                  product={currentProduct || product}
                  currentProduct={currentProduct}
                  products={products}
                  selectedLayerId={selectedLayerId}
                  transformedLayers={transformedLayers}
                  textContent={textContent}
                  fontSize={fontSize}
                  fontFamily={fontFamily}
                  textColor={textColor}
                  selectedColor={selectedColor}
                  selectedColorObj={selectedColorObj}
                  onTextContentChange={(value) => {
                    setTextContent(value);
                    if (selectedLayerId) {
                      const selectedLayer = transformedLayers.find(layer => layer.id === selectedLayerId);
                      if (selectedLayer && selectedLayer.type === 'text') {
                        const newLayers = transformedLayers.map(layer =>
                          layer.id === selectedLayerId
                            ? { ...layer, text: value }
                            : layer
                        );
                        setTransformedLayers(newLayers);
                      }
                    }
                  }}
                  onFontSizeChange={(value) => {
                    setFontSize(value);
                    if (selectedLayerId) {
                      const selectedLayer = transformedLayers.find(layer => layer.id === selectedLayerId);
                      if (selectedLayer && selectedLayer.type === 'text') {
                        const newLayers = transformedLayers.map(layer =>
                          layer.id === selectedLayerId
                            ? {
                              ...layer,
                              fontSize: value
                            }
                            : layer
                        );
                        setTransformedLayers(newLayers);
                      }
                    }
                  }}
                  onFontFamilyChange={(value) => {
                    setFontFamily(value);
                    if (selectedLayerId) {
                      const selectedLayer = transformedLayers.find(layer => layer.id === selectedLayerId);
                      if (selectedLayer && selectedLayer.type === 'text') {
                        const newLayers = transformedLayers.map(layer =>
                          layer.id === selectedLayerId
                            ? {
                              ...layer,
                              fontFamily: value
                            }
                            : layer
                        );
                        setTransformedLayers(newLayers);
                      }
                    }
                  }}
                  onTextColorChange={(value) => {
                    setTextColor(value);
                    if (selectedLayerId) {
                      const selectedLayer = transformedLayers.find(layer => layer.id === selectedLayerId);
                      if (selectedLayer && selectedLayer.type === 'text') {
                        const newLayers = transformedLayers.map(layer =>
                          layer.id === selectedLayerId
                            ? {
                              ...layer,
                              fill: value
                            }
                            : layer
                        );
                        setTransformedLayers(newLayers);
                      }
                    }
                  }}
                  onSelectedColorChange={setSelectedColor}
                  onTemplateSelect={handleTemplateSelect}
                  onAddTextElement={() => {
                    addTextElement();
                    // Auto-hide mobile sidebar when element is added from RightContentArea
                    if (isMobile) {
                      setIsMobileSidebarOpen(false);
                    }
                  }}
                  onAddShapeElement={(shape) => {
                    addShapeElement(shape);
                    // Auto-hide mobile sidebar when element is added from RightContentArea
                    if (isMobile) {
                      setIsMobileSidebarOpen(false);
                    }
                  }}
                  onAddFrameElement={(frameStyle, frameColor, frameSize) => {
                    addFrameElement(frameStyle, frameColor, frameSize);
                    // Auto-hide mobile sidebar when element is added from RightContentArea
                    if (isMobile) {
                      setIsMobileSidebarOpen(false);
                    }
                  }}
                  onAddImageElement={(imageUrl) => {
                    addImageElement(imageUrl);
                    // Auto-hide mobile sidebar when element is added from RightContentArea
                    if (isMobile) {
                      setIsMobileSidebarOpen(false);
                    }
                  }}
                  onSetBackgroundTexture={setBackgroundTexture}
                  onSetBackgroundImage={setBackgroundImage}
                  onUploadBackgroundImage={uploadBackgroundImage}
                  onSetBackgroundColor={setBackgroundColor}
                  onSetActiveTool={(tool: string) => {
                    if (tool === 'select' || tool === 'text' || tool === 'shape') {
                      setActiveTool(tool as 'select' | 'text' | 'shape');
                    }
                  }}
                  onLayerSelect={(layerId) => {
                    if (layerId.startsWith('template-')) {
                      setSelectedLayerId(layerId);
                    } else {
                      const layerExists = transformedLayers.some(layer => layer.id === layerId);
                      if (layerExists) {
                        setSelectedLayerId(layerId);
                      } else {
                        setSelectedLayerId(null);
                      }
                    }
                  }}
                  onTemplateUpdate={(updatedProduct) => {
                    setCurrentProduct(updatedProduct);
                  }}
                  onReorderLayers={reorderLayers}
                  onDeleteLayer={deleteLayer}
                  onProductSelect={handleProductSelect}
                />
              </div>
            </div>
          </div>
        )}

        {/* Desktop Layout */}
        {!isMobile && (
          <>
            {/* Left Sidebar - Tools */}
            <LeftSidebar
              selectedTool={selectedTool}
              onToolSelect={setSelectedTool}
            />

            {/* Right Content Area */}
            <RightContentArea
              selectedTool={selectedTool}
              product={currentProduct || product}
              currentProduct={currentProduct}
              products={products}
              selectedLayerId={selectedLayerId}
              transformedLayers={transformedLayers}
              textContent={textContent}
              fontSize={fontSize}
              fontFamily={fontFamily}
              textColor={textColor}
              selectedColor={selectedColor}
              selectedColorObj={selectedColorObj}
              onTextContentChange={(value) => {
                setTextContent(value);
                if (selectedLayerId) {
                  const selectedLayer = transformedLayers.find(layer => layer.id === selectedLayerId);
                  if (selectedLayer && selectedLayer.type === 'text') {
                    const newLayers = transformedLayers.map(layer =>
                      layer.id === selectedLayerId
                        ? { ...layer, text: value }
                        : layer
                    );
                    setTransformedLayers(newLayers);
                  }
                }
              }}
              onFontSizeChange={(value) => {
                setFontSize(value);
                if (selectedLayerId) {
                  const selectedLayer = transformedLayers.find(layer => layer.id === selectedLayerId);
                  if (selectedLayer && selectedLayer.type === 'text') {
                    const newLayers = transformedLayers.map(layer =>
                      layer.id === selectedLayerId
                        ? {
                          ...layer,
                          fontSize: value
                        }
                        : layer
                    );
                    setTransformedLayers(newLayers);
                  }
                }
              }}
              onFontFamilyChange={(value) => {
                setFontFamily(value);
                if (selectedLayerId) {
                  const selectedLayer = transformedLayers.find(layer => layer.id === selectedLayerId);
                  if (selectedLayer && selectedLayer.type === 'text') {
                    const newLayers = transformedLayers.map(layer =>
                      layer.id === selectedLayerId
                        ? {
                          ...layer,
                          fontFamily: value
                        }
                        : layer
                    );
                    setTransformedLayers(newLayers);
                  }
                }
              }}
              onTextColorChange={(value) => {
                setTextColor(value);
                if (selectedLayerId) {
                  const selectedLayer = transformedLayers.find(layer => layer.id === selectedLayerId);
                  if (selectedLayer && selectedLayer.type === 'text') {
                    const newLayers = transformedLayers.map(layer =>
                      layer.id === selectedLayerId
                        ? {
                          ...layer,
                          fill: value
                        }
                        : layer
                    );
                    setTransformedLayers(newLayers);
                  }
                }
              }}
              onSelectedColorChange={setSelectedColor}
              onTemplateSelect={handleTemplateSelect}
              onAddTextElement={() => {
                addTextElement();
                // Auto-hide mobile sidebar when element is added from RightContentArea
                if (isMobile) {
                  setIsMobileSidebarOpen(false);
                }
              }}
              onAddShapeElement={(shape) => {
                addShapeElement(shape);
                // Auto-hide mobile sidebar when element is added from RightContentArea
                if (isMobile) {
                  setIsMobileSidebarOpen(false);
                }
              }}
              onAddFrameElement={(frameStyle, frameColor, frameSize) => {
                addFrameElement(frameStyle, frameColor, frameSize);
                // Auto-hide mobile sidebar when element is added from RightContentArea
                if (isMobile) {
                  setIsMobileSidebarOpen(false);
                }
              }}
              onAddImageElement={(imageUrl) => {
                addImageElement(imageUrl);
                // Auto-hide mobile sidebar when element is added from RightContentArea
                if (isMobile) {
                  setIsMobileSidebarOpen(false);
                }
              }}
              onSetBackgroundTexture={setBackgroundTexture}
              onSetBackgroundImage={setBackgroundImage}
              onUploadBackgroundImage={uploadBackgroundImage}
              onSetBackgroundColor={setBackgroundColor}
              onSetActiveTool={(tool: string) => {
                if (tool === 'select' || tool === 'text' || tool === 'shape') {
                  setActiveTool(tool as 'select' | 'text' | 'shape');
                }
              }}
              onLayerSelect={(layerId) => {
                if (layerId.startsWith('template-')) {
                  setSelectedLayerId(layerId);
                } else {
                  const layerExists = transformedLayers.some(layer => layer.id === layerId);
                  if (layerExists) {
                    setSelectedLayerId(layerId);
                  } else {
                    setSelectedLayerId(null);
                  }
                }
              }}
              onTemplateUpdate={(updatedProduct) => {
                setCurrentProduct(updatedProduct);
              }}
              onReorderLayers={reorderLayers}
              onDeleteLayer={deleteLayer}
              onUpdateLayer={(layerId: string, updatedLayer: any) => {
                const newLayers = transformedLayers.map(layer =>
                  layer.id === layerId ? updatedLayer : layer
                );
                setTransformedLayers(newLayers);
                saveToHistory(newLayers);
              }}
              onProductSelect={handleProductSelect}
            />


          </>
        )}

        {/* Center Canvas Area - Full width on mobile */}
        <div className={`${isMobile ? 'w-full' : 'flex-1'} flex flex-col bg-white`}>
          {/* Canvas Toolbar */}
          <CanvasToolbar
            historyIndex={fabricHistIndex}
            historyLength={fabricHistLength}
            selectedLayerId={selectedLayerId}
            hasActiveSelection={hasFabricActive}
            onUndo={() => { window.dispatchEvent(new CustomEvent('giftmkr:undo')); undo(); }}
            onRedo={() => { window.dispatchEvent(new CustomEvent('giftmkr:redo')); redo(); }}
            onMoveLayerForward={moveLayerForward}
            onMoveLayerBackward={moveLayerBackward}
            onAlignLeft={alignLayerLeft}
            onAlignCenter={alignLayerCenter}
            onAlignRight={alignLayerRight}
            onAlignTop={alignLayerTop}
            onAlignMiddle={alignLayerMiddle}
            onAlignBottom={alignLayerBottom}
            isMobile={isMobile} 
          />
          {/* Canvas Component */}
          <Canvas
            canvasSize={canvasSize}
            selectedColorObj={selectedColorObj}
            transformedLayers={transformedLayers}
            selectedLayerId={selectedLayerId}
            printArea={printArea}
            onCanvasClick={handleCanvasClick}
            onLayerClick={handleLayerClick}
            onDragStart={handleDragStart}
            onTransformStart={handleTransformStart}
            elements={elements}
            selectedElement={selectedElement}
            onElementClick={setSelectedElement}
            // canvasRef={canvasRef}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            isMobile={isMobile}
            onUpdateLayerPosition={updateLayerPosition}
            onUpdateLayerSize={updateLayerSize}
            onUpdateLayerRotation={updateLayerRotation}
            onLayerTouchStart={(e, layerId) => {
              // Handle layer touch start for mobile
              if (isMobile) {
                setSelectedLayerId(layerId);
              }
            }}
            onLayerTouchMove={(e, layerId) => {
              // Handle layer touch move for mobile
              if (isMobile && selectedLayerId === layerId) {
                // Handle touch-based dragging
                const touch = e.touches[0];
                const rect = canvasRef.current?.getBoundingClientRect();
                if (rect) {
                  const mouseX = touch.clientX - rect.left;
                  const mouseY = touch.clientY - rect.top;
                  // Update layer position based on touch
                  const selectedLayer = transformedLayers.find(layer => layer.id === layerId);
                  if (selectedLayer) {
                    updateLayerPosition(layerId, mouseX - selectedLayer.width / 2, mouseY - selectedLayer.height / 2);
                  }
                }
              }
            }}
            onLayerTouchEnd={(e, layerId) => {
              // Handle layer touch end for mobile
              if (isMobile) {
                // Save to history when touch ends
                saveToHistory(transformedLayers);
              }
            }}
            onLayerTransformStart={(e, layerId, controlPoint) => {
              // Handle layer transform start for mobile
              if (isMobile) {
                setSelectedLayerId(layerId);
                // Handle touch-based scaling
                const touch = e.touches[0];
                const rect = canvasRef.current?.getBoundingClientRect();
                if (rect) {
                  const mouseX = touch.clientX - rect.left;
                  const mouseY = touch.clientY - rect.top;
                  const selectedLayer = transformedLayers.find(layer => layer.id === layerId);
                  if (selectedLayer) {
                    // Calculate scale based on touch position
                    const centerX = selectedLayer.left + selectedLayer.width / 2;
                    const centerY = selectedLayer.top + selectedLayer.height / 2;
                    const distance = Math.sqrt(
                      Math.pow(mouseX - centerX, 2) + Math.pow(mouseY - centerY, 2)
                    );
                    const scale = Math.max(0.5, Math.min(3, distance / 100));
                    updateLayerSize(layerId, selectedLayer.width * scale, selectedLayer.height * scale);
                  }
                }
              }
            }}
          />
        </div>

        {/* Right Sidebar - Properties - Desktop Only */}
        {!isMobile && (
          <PropertiesPanel
            selectedLayerId={selectedLayerId}
            transformedLayers={transformedLayers}
            selectedElement={selectedElement}
            elements={elements}
            selectedSize={selectedSize}
            product={currentProduct || product}
            onDeleteLayer={deleteLayer}
            onDeselectLayer={() => setSelectedLayerId(null)}
            onUpdateLayerPosition={updateLayerPosition}
            onUpdateLayerSize={updateLayerSize}
            onUpdateTransformedLayers={setTransformedLayers}
            onToggleElementVisibility={toggleElementVisibility}
            onDeleteElement={deleteElement}
            onUpdateElementPosition={updateElementPosition}
            onSelectedSizeChange={setSelectedSize}
          />
        )}
      </div>

      {/* Mobile Preview Button */}
      {isMobile && (
        <button
          onClick={() => setIsPreviewWindowVisible(true)}
          className="fixed bottom-20 right-4 z-50 text-white p-3 rounded-full shadow-lg transition-colors"
          style={{ backgroundColor: '#68aeb0' }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#7bb8ba'}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#68aeb0'}
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
          </svg>
        </button>
      )}

      {/* Preview Window - Floating in bottom-right corner */}
      <PreviewWindow
        isVisible={isPreviewWindowVisible}
        onClose={() => setIsPreviewWindowVisible(false)}
        selectedTemplate={selectedTemplate}
        loadedProduct={currentProduct}
        code={cacheKey || ''}
      />
 
      {/* Save Design Modal */}
      <SaveDesignModal
        isOpen={saveDesignModal}
        onClose={() => setSaveDesignModal(false)}
        onSave={handleSaveDesign}
        isSaving={isSaving}
      />
    </div>
  );
};

export default DesignEditor;
